import { type MNTableContent, TableAxis, TableOperation } from '../../../../common/table/TableTypes';
import { isNumber } from 'lodash';
import { continueCellFormatting } from '../utils/dataUtils/grid/tableGridFormattingContinuationUtils';
import { suspendHyperFormulaGridOperationsForSheet } from '../utils/hyperFormulaUtils/hyperFormulaSuspendGridOperationsUtils';
import { convertHotInstanceToRaw } from '../utils/hotInstanceUtils/persistence/hotTablePersistenceUtils';
import { applyHotTableCellMetaInRange } from '../utils/hotInstanceUtils/meta/hotTableMetaRangeUtils';
import { repositionAutofillHandle } from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';
import {
    captureHyperFormulaUndoRedoStack,
    type HyperFormulaUndoRedoStacks,
} from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoHyperFormulaUtils';
import { reconcileMovedGridWithUndoRedo } from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoMoveUtils';
import { getCellRangeFromStartAndSize } from '../utils/selectionUtils/tableSelectionRangeUtils';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';

export const GRID_UPDATE_PLUGIN_NAME = 'GridUpdate';

/**
 * Keeps Milanote-specific cell metadata and undo/redo state in sync with HOT row/column updates.
 */
export default class GridUpdateHotPlugin extends MilanoteHotPlugin {
    private pendingBeforeMove: {
        snapshot: MNTableContent;
        hyperFormulaStack: HyperFormulaUndoRedoStacks | null;
    } | null = null;

    private resumeHyperFormulaGridOperations: (() => void) | null = null;

    static get PLUGIN_KEY(): string {
        return GRID_UPDATE_PLUGIN_NAME;
    }

    /**
     * Clears any move-scoped HyperFormula suspension that was started in beforeGridMovement.
     */
    private resumeHyperFormulaGridOperationsSuspension(): void {
        this.resumeHyperFormulaGridOperations?.();
        this.resumeHyperFormulaGridOperations = null;
    }

    enablePlugin(): void {
        this.addHook('afterCreateRow', (index, amount, source) =>
            this.afterGridAltered(TableAxis.ROW, TableOperation.INSERT, index, amount, source),
        );
        this.addHook('afterCreateCol', (index, amount, source) =>
            this.afterGridAltered(TableAxis.COL, TableOperation.INSERT, index, amount, source),
        );
        this.addHook('afterRemoveRow', (index, amount, _physicalRows, source) =>
            this.afterGridAltered(TableAxis.ROW, TableOperation.REMOVE, index, amount, source),
        );
        this.addHook('afterRemoveCol', (index, amount, _physicalColumns, source) =>
            this.afterGridAltered(TableAxis.COL, TableOperation.REMOVE, index, amount, source),
        );
        this.addHook('beforeRowMove', (movedIndices, finalIndex, dropIndex, movePossible) =>
            this.beforeGridMovement(movedIndices, finalIndex, dropIndex, movePossible),
        );
        this.addHook('beforeColumnMove', (movedIndices, finalIndex, dropIndex, movePossible) =>
            this.beforeGridMovement(movedIndices, finalIndex, dropIndex, movePossible),
        );
        this.addHook('afterRowMove', (movedIndices, finalIndex, dropIndex, movePossible, orderChanged) =>
            this.afterGridMovement(TableAxis.ROW, movedIndices, finalIndex, dropIndex, movePossible, orderChanged),
        );
        this.addHook('afterColumnMove', (movedIndices, finalIndex, dropIndex, movePossible, orderChanged) =>
            this.afterGridMovement(TableAxis.COL, movedIndices, finalIndex, dropIndex, movePossible, orderChanged),
        );

        super.enablePlugin();
    }

    /**
     * Ensures move-scoped suppression is released if HOT disables this plugin mid-lifecycle.
     */
    disablePlugin(): void {
        this.resumeHyperFormulaGridOperationsSuspension();
        super.disablePlugin();
    }

    /**
     * Reconciles HOT grid structure changes with the cell data snapshot that Redux persistence will later save.
     */
    private afterGridAltered(
        axis: TableAxis,
        operation: TableOperation,
        index: number,
        amount: number,
        _source?: string,
    ): void {
        const hotInstance = this.milanoteHot;
        const { gridSize, hotTableContainerRef } = hotInstance.milanoteProps;

        repositionAutofillHandle(hotInstance, hotTableContainerRef);

        if (operation !== TableOperation.INSERT) return;

        const rawTableContent = convertHotInstanceToRaw(hotInstance, gridSize);
        const formattedData = continueCellFormatting({
            dataArray: rawTableContent.data,
            colIndex: axis === TableAxis.COL ? index : 0,
            nColsAdded: axis === TableAxis.COL ? amount : 0,
            rowIndex: axis === TableAxis.ROW ? index : 0,
            nRowsAdded: axis === TableAxis.ROW ? amount : 0,
        });

        const nRows = formattedData.length;
        const nCols = formattedData[0]?.length || 0;

        applyHotTableCellMetaInRange({
            hotInstance,
            cellRange: getCellRangeFromStartAndSize(
                axis === TableAxis.COL
                    ? { start: { row: 0, col: index }, size: { row: nRows, col: amount } }
                    : { start: { row: index, col: 0 }, size: { row: amount, col: nCols } },
            ),
            getCellMeta: ({ row, col }) => ({
                cellData: formattedData[row][col],
            }),
        });
    }

    /**
     * Stores the pre-move snapshot before HOT and HyperFormula update any index mapper state.
     */
    private beforeGridMovement(
        _movedIndices: number[],
        _finalIndex: number,
        _dropIndex: number | undefined,
        movePossible: boolean,
    ): void {
        const { gridSize } = this.milanoteHot.milanoteProps;

        this.resumeHyperFormulaGridOperationsSuspension();

        if (!movePossible) {
            this.pendingBeforeMove = null;
            return;
        }

        const formulasPlugin = this.milanoteHot.getPlugin<'formulas'>('formulas');
        const sheetId = formulasPlugin?.sheetId;
        const hfInstance = formulasPlugin?.engine;

        if (isNumber(sheetId) && hfInstance) {
            // Temporarily suspend HyperFormula's grid operations as they were causing an error during row/column move.
            //
            // These grid operations:
            // - are causing errors because they assume HyperFormula’s sheet dimensions match
            //   Handsontable’s visible grid dimensions, which is untrue.
            // - are not essential because in afterGridMovement, we rebuild the moved table structure from its own
            //   HOT snapshot, then rehydrate both HOT and HyperFormula from that final reordered snapshot.
            this.resumeHyperFormulaGridOperations = suspendHyperFormulaGridOperationsForSheet(hfInstance, sheetId);
        }

        this.pendingBeforeMove = {
            snapshot: convertHotInstanceToRaw(this.milanoteHot, gridSize),
            hyperFormulaStack: captureHyperFormulaUndoRedoStack(this.milanoteHot),
        };
    }

    /**
     * Rewrites moved rows or columns into persisted order and records a snapshot-based undo action.
     */
    private afterGridMovement(
        axis: TableAxis,
        movedIndices: number[],
        finalIndex: number,
        _dropIndex: number | undefined,
        movePossible: boolean,
        orderChanged: boolean,
    ): void {
        const beforeSnapshot = this.pendingBeforeMove?.snapshot;
        const beforeHyperFormulaUndoRedoStack = this.pendingBeforeMove?.hyperFormulaStack;
        const { gridSize } = this.milanoteHot.milanoteProps;

        this.pendingBeforeMove = null;

        try {
            if (!movePossible || !orderChanged || !beforeSnapshot) return;

            reconcileMovedGridWithUndoRedo({
                hotInstance: this.milanoteHot,
                beforeSnapshot,
                axis,
                movedIndices,
                finalIndex,
                gridSize,
                beforeHyperFormulaUndoRedoStack,
            });
        } finally {
            this.resumeHyperFormulaGridOperationsSuspension();
        }
    }
}
